// Notification feed helper. Server functions and client mutations both call pushNotif() to add a
// note to a business's in-app feed (data.business.notifications). Notes are kept newest-first and
// capped so the doc can't grow without bound. Delivery to WhatsApp/email later reads the same feed.
import type { BusinessData, Notification } from "./types.ts";

const MAX_NOTIFS = 200;
let seq = 0;

export type NewNotif = {
  audience: "owner" | "mfr";
  kind: string;
  title: string;
  body?: string;
  tone?: Notification["tone"];
  runId?: string;
  at?: number;
};

export function pushNotif(data: BusinessData, note: NewNotif): Notification {
  const at = note.at ?? Date.now();
  const n: Notification = {
    id: `ntf_${at.toString(36)}_${(seq++).toString(36)}`,
    at,
    audience: note.audience,
    kind: note.kind,
    title: note.title,
    body: note.body,
    tone: note.tone || "info",
    runId: note.runId,
    read: false,
  };
  const list = data.business.notifications || (data.business.notifications = []);
  list.unshift(n);
  if (list.length > MAX_NOTIFS) list.length = MAX_NOTIFS;
  return n;
}

// Idempotency gate for the daily scheduled alerts. Returns true (and stamps the ledger) only if this
// `key` hasn't fired within `cooldownMs` — so a persistent condition re-nudges at most once per window
// instead of every run. Prunes stamps older than 60 days so the ledger can't grow forever.
export function alertOnce(data: BusinessData, key: string, cooldownMs: number, now: number): boolean {
  const led = data.business.alertsSent || (data.business.alertsSent = {});
  const last = led[key] || 0;
  if (now - last < cooldownMs) return false;
  led[key] = now;
  for (const k of Object.keys(led)) if (now - led[k] > 60 * 86400000) delete led[k];
  return true;
}

// Mark notifications read for one audience — all, or a specific set of ids.
export function markNotifsRead(data: BusinessData, audience: "owner" | "mfr", ids?: string[]): void {
  const set = ids && ids.length ? new Set(ids) : null;
  for (const n of data.business.notifications || []) {
    if (n.audience === audience && (!set || set.has(n.id))) n.read = true;
  }
}
